<script setup lang="ts">
import { useTitle } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, watch } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { toast, Toaster } from 'vue-sonner'

import { client } from './client/client.gen'
import { getCachedLocalStorage } from './composables/useAppStorage'
import { useTheme } from './composables/useTheme'
import { TOASTER_DEFAULT_DURATION, TOASTER_DURATION_KEY } from './constants/ui'
import { useRegionStore } from './stores/region'
import { useUserStore } from './stores/user'

const router = useRouter()
const userStore = useUserStore()
const { isAuthenticated } = storeToRefs(userStore)
const regionStore = useRegionStore()
const { isDark } = useTheme()

const toastDuration = getCachedLocalStorage<number>(TOASTER_DURATION_KEY, TOASTER_DEFAULT_DURATION)

watch(isAuthenticated, (isAuth, wasAuth) => {
  if (wasAuth && !isAuth) {
    toast.error('请重新登录')
    router.push('/login')
  }
})

const title = useTitle()
watch(() => regionStore.currentRegion, () => {
  client.setConfig({
    baseUrl: regionStore.currentRegion.apiBaseUrl,
  })

  title.value = `Spatialwalk Dashboard - ${regionStore.currentRegion.name}`
}, { immediate: true })

// 点击 toast 时关闭所有 toast
function handleToastClick(event: MouseEvent) {
  const target = event.target as HTMLElement
  // 检查点击的是否是 toast 元素（不包括 action/cancel 按钮）
  const toastElement = target.closest('[data-sonner-toast]')
  const isButton = target.closest('[data-button]')
  if (toastElement && !isButton) {
    // 关闭所有 toast
    toast.dismiss()
  }
}

onMounted(() => {
  document.addEventListener('click', handleToastClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleToastClick)
})
</script>

<template>
  <div class="min-h-screen bg-background text-foreground transition-colors duration-300 ease-in-out">
    <Toaster position="top-center" :expand="true" :rich-colors="true" :duration="toastDuration" :theme="isDark ? 'dark' : 'light'" />
    <RouterView v-slot="{ Component }" :key="$route.fullPath">
      <KeepAlive>
        <component :is="Component" />
      </KeepAlive>
    </RouterView>
  </div>
</template>
